import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Mail, Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { useDeliverable } from "@/hooks/useDeliverable";
import { PageHeader } from "@/components/PageParts";
import { Button, Card, Input, Textarea } from "@/components/ui";

export default function Contact() {
  const { lang } = useAuth();
  const isAr = lang === "ar";
  useEffect(() => {
    document.title = isAr
      ? "تواصل مع أوتوبيم لوجيك - الدعم والمبيعات"
      : "Contact AutoBIM Logic - Support & Sales";
    const md = document.querySelector('meta[name="description"]');
    if (md) md.setAttribute("content", isAr
      ? "تواصل مع فريق أوتوبيم لوجيك عبر البريد أو واتساب أو تيليجرام للدعم والاستفسارات حول مولّد المخططات المعمارية والإنشائية."
      : "Get in touch with the AutoBIM Logic team by email, WhatsApp or Telegram for support and questions about the architectural & structural plan generator.");
  }, [isAr]);
  const { data: methods } = useDeliverable(() => api.listContactMethods(), []);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [back, setBack] = useState("");
  const [busy, setBusy] = useState(false);

  async function send() {
    if (!subject || !body) return;
    setBusy(true);
    try {
      await api.sendContactMessage(subject, body, back);
      setSubject(""); setBody(""); setBack("");
      toast.success(isAr ? "تم إرسال رسالتك" : "Message sent");
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-6">
      <PageHeader eyebrow={isAr ? "تواصل" : "Contact"} title={isAr ? "تواصل معنا" : "Contact us"} />
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <p className="mb-3 flex items-center gap-2 text-sm font-black"><Send size={16} /> {isAr ? "أرسل رسالة" : "Send a message"}</p>
          <div className="space-y-3">
            <Input label={isAr ? "الموضوع" : "Subject"} value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="contact-subject" />
            <Textarea label={isAr ? "الرسالة" : "Message"} value={body} onChange={(e) => setBody(e.target.value)} rows={5} data-testid="contact-body" />
            <Input label={isAr ? "وسيلة تواصل (اختياري)" : "Contact back (optional)"} value={back} onChange={(e) => setBack(e.target.value)} data-testid="contact-back" />
            <Button loading={busy} onClick={send} data-testid="contact-send"><Send size={15} /> {isAr ? "إرسال" : "Send"}</Button>
          </div>
        </Card>
        <Card>
          <p className="mb-3 flex items-center gap-2 text-sm font-black"><Mail size={16} /> {isAr ? "قنوات التواصل" : "Contact channels"}</p>
          <div className="space-y-2">
            {(methods?.items || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">{isAr ? "استخدم النموذج على اليسار." : "Use the form on the left."}</p>
            ) : (
              (methods.items).map((m, i) => (
                <div key={i} className="rounded-lg bg-black/20 p-3 text-sm">
                  <div className="font-bold">{m.label || m.type}</div>
                  <div className="text-xs text-muted-foreground">{m.value}</div>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
